/**
 * Payload validation at the socket boundary.
 *
 * A malformed payload is dropped, never forwarded to the store: a half-parsed
 * round would show the player a multiplier the server never produced. Dropping
 * is safe because `state:sync` re-establishes the full truth on reconnect.
 */
import type { ZodType } from "zod";
import { SERVER_EVENT_SCHEMAS, type ServerEventName, type ServerEventPayload } from "./eventSchemas.js";

export interface InvalidPayloadReport {
  event: string;
  payload: unknown;
  message: string;
}

export interface PayloadParserOptions {
  /** Called instead of the console reporter (tests, error tracking). */
  onInvalid?: (report: InvalidPayloadReport) => void;
}

export type PayloadParser = <K extends ServerEventName>(
  event: K,
  payload: unknown,
) => ServerEventPayload<K> | null;

export function createPayloadParser(options: PayloadParserOptions = {}): PayloadParser {
  const report = options.onInvalid ?? reportToConsole;

  return <K extends ServerEventName>(event: K, payload: unknown): ServerEventPayload<K> | null => {
    const schema = SERVER_EVENT_SCHEMAS[event] as ZodType<ServerEventPayload<K>>;
    const result = schema.safeParse(payload);
    if (result.success) return result.data;
    report({ event, payload, message: formatIssues(result.error) });
    return null;
  };
}

function formatIssues(error: { issues: readonly { path: PropertyKey[]; message: string }[] }): string {
  return error.issues
    .slice(0, 5)
    .map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`)
    .join("; ");
}

function reportToConsole(report: InvalidPayloadReport): void {
  // Kept in production too: a contract mismatch between web and api is the kind
  // of bug a buyer would otherwise only see as "the game froze".
  console.error(`[crashx] dropped invalid "${report.event}" payload — ${report.message}`);
}
